import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment';
import { BASE_API, USER_TOKEN } from 'config/consts';
import { deleteActionDraft } from '../../actionsDrafts/actionsDraftsScripts';

const ACTION_TYPES = {
  ONLINE: 'online',
  SCHEDULED: 'scheduled',
  DRAFTS: 'drafts',
  CLOSED: 'closed',
};

export const useActionsService = (auth, selectedOption, lazyState) => {
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);

  const stationId = auth?.user?.establishments?.[0]?.stations?.[0]?.id;

  const formatActionData = useCallback((items) => {
    return items.map((item) => ({
      ...item,
      createdAtDate: moment(item.createdAt).format('DD/MM/YYYY HH:mm:ss'),
      startDate: moment(item.start).format('DD/MM/YYYY HH:mm:ss'),
      endDate: moment(item.end).format('DD/MM/YYYY HH:mm:ss'),
    }));
  }, []);

  const buildApiUrl = useCallback((type, stationId, lazyParams, isScheduled = 0) => {
    // Corrigir o cálculo do offset - usar first ao invés de page
    const offset = lazyParams.first || 0;

    // Definir ordenação padrão se não especificada
    const sortField = lazyParams.sortField || 'createdAt';
    const sortOrder = lazyParams.sortOrder || 'DESC';

    const baseParams = `where[station_id]=${stationId}&where[limit]=${
      lazyParams.rows
    }&where[offset]=${offset}&where[order]=${sortField} ${sortOrder}&where[filters]=${JSON.stringify(
      lazyParams.filters
    )}`;

    let finalUrl;
    switch (type) {
      case ACTION_TYPES.ONLINE:
      case ACTION_TYPES.SCHEDULED:
        finalUrl = `${BASE_API}/actions-list-table/?where[active]=1&where[is_draft]=0&where[station_id]=${stationId}&where[is_scheduled]=${isScheduled}&where[is_tanque_cheio]=0${
          type === ACTION_TYPES.ONLINE ? '&where[not_expired]=1' : ''
        }&${baseParams}`;
        break;
      case ACTION_TYPES.DRAFTS:
        finalUrl = `${BASE_API}/actions-list-table/?where[active]=0&where[is_draft]=1&where[is_tanque_cheio]=0&${baseParams}`;
        break;
      case ACTION_TYPES.CLOSED:
        finalUrl =
          `${BASE_API}/archived-actions/?` +
          `where[station_id]=${stationId}` +
          `&where[limit]=${lazyParams.rows}` +
          `&where[offset]=${offset}` +
          `&where[order]=${sortField} ${sortOrder}` +
          `&where[is_tanque_cheio]=0` +
          `&where[filters]=${JSON.stringify(lazyParams.filters)}`;
        break;
      default:
        finalUrl = '';
    }

    return finalUrl;
  }, []);

  const buildTotalUrl = useCallback((type, stationId, lazyParams, isScheduled = 0) => {
    const baseParams = `where[station_id]=${stationId}&where[filters]=${JSON.stringify(
      lazyParams.filters
    )}`;

    switch (type) {
      case ACTION_TYPES.ONLINE:
      case ACTION_TYPES.SCHEDULED:
        return `${BASE_API}/actions/total/?where[active]=1&where[is_draft]=0&where[station_id]=${stationId}&where[is_scheduled]=${isScheduled}&where[is_tanque_cheio]=0&${baseParams}`;
      case ACTION_TYPES.DRAFTS:
        return `${BASE_API}/actions/total/?where[active]=0&where[is_draft]=1&where[is_tanque_cheio]=0&${baseParams}`;
      case ACTION_TYPES.CLOSED:
        return `${BASE_API}/archived-actions/total?where[station_id]=${stationId}&where[is_draft]=0&where[is_tanque_cheio]=0&${baseParams}`;
      default:
        return '';
    }
  }, []);

  const fetchData = useCallback(
    async (type, stationId, lazyParams) => {
      if (!stationId || !type) return;

      try {
        setLoading(true);

        const isScheduled = type === ACTION_TYPES.SCHEDULED ? 1 : 0;
        const dataUrl = buildApiUrl(type, stationId, lazyParams, isScheduled);
        const totalUrl = buildTotalUrl(type, stationId, lazyParams, isScheduled);

        // DEBUG: Log URLs e station_id para verificar filtros
        // console.log('DEBUG - Filtro de Ações:');
        // console.log('Station ID:', stationId);
        // console.log('Type:', type);
        // console.log('Data URL:', dataUrl);
        // console.log('Total URL:', totalUrl);

        const [dataResponse, totalResponse] = await Promise.all([
          axios.get(dataUrl, {
            headers: {
              Authorization: `${JSON.parse(localStorage.getItem(USER_TOKEN))}`,
            },
          }),
          axios.get(totalUrl, {
            headers: {
              Authorization: `${JSON.parse(localStorage.getItem(USER_TOKEN))}`,
            },
          }),
        ]);

        // DEBUG: Log dados retornados para verificar se filtro está funcionando
        // console.log('📊 DEBUG - Dados Retornados:');
        // console.log('Total de ações retornadas:', dataResponse.data.length);
        // console.log(
        //   'Ações com station_id:',
        //   dataResponse.data.map((action) => ({
        //     id: action.id,
        //     name: action.name,
        //     station_id: action.station_id,
        //     partner_id: action.partner_id,
        //   }))
        // );

        const formattedData = formatActionData(dataResponse.data);
        setData(formattedData);
        setTotalRecords(totalResponse.data.total || totalResponse.data.length || 0);
      } catch (error) {
        console.error(`Error fetching ${type} actions:`, error);
        setData([]);
        setTotalRecords(0);
      } finally {
        setLoading(false);
      }
    },
    [buildApiUrl, buildTotalUrl, formatActionData]
  );

  const handleDeleteDraft = useCallback(
    async (id) => {
      try {
        setButtonLoading(true);

        await axios.delete(`${BASE_API}/actions-drafts/${id}`, {
          headers: {
            Authorization: `${JSON.parse(localStorage.getItem(USER_TOKEN))}`,
          },
        });

        // Refresh data after deletion
        await fetchData(selectedOption, stationId, lazyState);
      } catch (error) {
        console.error('Error deleting draft:', error);
        throw error;
      } finally {
        setButtonLoading(false);
      }
    },
    [selectedOption, stationId, lazyState, fetchData]
  );

  const refreshData = useCallback(() => {
    fetchData(selectedOption, stationId, lazyState);
  }, [fetchData, selectedOption, stationId, lazyState]);

  // Fetch data when dependencies change
  useEffect(() => {
    fetchData(selectedOption, stationId, lazyState);
  }, [fetchData, selectedOption, stationId, lazyState]);

  return {
    data,
    totalRecords,
    loading,
    buttonLoading,
    handleDeleteDraft,
    refreshData,
  };
};
